"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { List, X } from "lucide-react";
import { usePublicPathname } from "@/lib/public-pathname";

type Entry = { id: string; label: string; el: HTMLElement };

const MIN_SECTIONS = 5;
const SECTIONS = new Set(["treatments", "eye-conditions"]);
const SPY_OFFSET = 140;

function label(heading: HTMLElement): string {
  return (heading.innerText || "").replace(/\s+/g, " ").trim();
}

export default function PageOutline() {
  const pathname = usePublicPathname();
  const enabled = pathname.split("/").some((seg) => SECTIONS.has(seg));
  const [entries, setEntries] = useState<Entry[]>([]);
  const [current, setCurrent] = useState(0);
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLElement>(null);

  const collect = useCallback(() => {
    const main = document.getElementById("main-content");
    if (!main) return;
    const found: Entry[] = [];
    main.querySelectorAll<HTMLElement>("h2").forEach((heading, i) => {
      if (heading.closest("[data-outline-skip]")) return;
      const text = label(heading);
      if (!text) return;
      found.push({ id: `outline-${i}`, label: text, el: heading });
    });
    setEntries(found.length >= MIN_SECTIONS ? found : []);
  }, []);

  const [lastPath, setLastPath] = useState(pathname);
  if (lastPath !== pathname) {
    setLastPath(pathname);
    setOpen(false);
  }

  useEffect(() => {
    if (!enabled) return;
    const frame = requestAnimationFrame(collect);
    const timer = setTimeout(collect, 600);
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(timer);
    };
  }, [collect, enabled, pathname]);

  useEffect(() => {
    if (!enabled || entries.length === 0) return;
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const y = window.scrollY + SPY_OFFSET;
        let active = 0;
        entries.forEach((entry, i) => {
          if (entry.el.getBoundingClientRect().top + window.scrollY <= y) active = i;
        });
        setCurrent(active);

        const legal = document.querySelector("[data-footer-legal]");
        if (legal) {
          const r = legal.getBoundingClientRect();
          if (r.top < window.innerHeight && r.bottom > window.innerHeight - 100) {
            setVisible(false);
            setOpen(false);
            return;
          }
        }
        setVisible(true);
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [enabled, entries]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      triggerRef.current?.focus();
    };
    const onPointer = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector('[aria-current="true"]')?.scrollIntoView({ block: "nearest" });
  }, [open, current]);

  const go = (entry: Entry) => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const top = entry.el.getBoundingClientRect().top + window.scrollY - SPY_OFFSET + 40;
    window.scrollTo({ top, behavior: reduced ? "auto" : "smooth" });
  };

  if (!enabled || entries.length === 0) return null;

  return (
    <div
      ref={rootRef}
      data-floating-dock
      className="pointer-events-none fixed bottom-8 left-8 z-40 hidden md:block"
      aria-hidden={!visible}
      inert={!visible}
    >
      <div
        className={`flex flex-col items-start gap-2 transition-all duration-300 ${
          visible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
        }`}
      >
        {open ? (
          <nav
            ref={listRef}
            aria-label="On this page"
            id="page-outline-panel"
            className="bg-navy-03/75 pointer-events-auto max-h-[min(60vh,30rem)] w-68 scrollbar-none overflow-y-auto rounded-2xl py-3 pr-3 pl-4 shadow-[0_20px_48px_-12px_rgba(0,0,0,0.45)] ring-1 ring-white/15 backdrop-blur-2xl backdrop-saturate-150"
          >
            <ul className="flex flex-col">
              {entries.map((entry, i) => (
                <li key={entry.id}>
                  <button
                    type="button"
                    onClick={() => go(entry)}
                    aria-current={i === current ? "true" : undefined}
                    className={`block w-full cursor-pointer border-l-2 py-1.5 pr-2 pl-3 text-left text-[13px] leading-relaxed transition-colors ${
                      i === current
                        ? "border-teal-05 text-white"
                        : "border-white/12 text-white/60 hover:border-white/40 hover:text-white/90"
                    }`}
                  >
                    {entry.label}
                  </button>
                </li>
              ))}
            </ul>
          </nav>
        ) : null}

        <button
          ref={triggerRef}
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls="page-outline-panel"
          className="bg-navy-03/75 pointer-events-auto flex h-12 cursor-pointer items-center gap-2.5 rounded-full pr-5 pl-4 text-white/85 shadow-[0_20px_48px_-12px_rgba(0,0,0,0.45)] ring-1 ring-white/15 backdrop-blur-2xl backdrop-saturate-150 transition-colors hover:text-white"
        >
          {open ? (
            <X className="size-[18px] shrink-0" strokeWidth={2} aria-hidden />
          ) : (
            <List className="size-[18px] shrink-0" strokeWidth={2} aria-hidden />
          )}
          <span className="text-sm font-semibold tracking-wide whitespace-nowrap">
            On this page
          </span>
          <span className="text-teal-03 text-sm font-semibold tabular-nums">
            {current + 1}/{entries.length}
          </span>
        </button>
      </div>
    </div>
  );
}
